import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { AppThunk } from 'store'
import { createApiAction, ApiAction, MiddlewareApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import {
  AddVirtualEventWaitListPersonsModel,
  MoveParticipantsToPreferredScheduleModel,
  RemoveVirtualEventWaitListPersonsModel,
} from 'modules/virtualClassrooms/common/types/waitList'
import {
  getListVirtualEventWaitListPersons,
  addVirtualEventWaitListPersons,
  moveParticipantsToPreferredSchedule,
  removeVirtualEventWaitListPersons,
} from 'modules/virtualClassrooms/manager/EventDetails/api/eventDetailsService'
import {
  WaitListState,
  ApiListVirtualEventWaitListPersonsResponse,
  ReloadListVirtualEventWaitListPersonsParams,
} from './types'

const initialState: WaitListState = {
  listVirtualEventWaitListPersons: {},
  addVirtualEventWaitListPersonsRequest: {},
  moveParticipantsToPreferredScheduleRequest: {},
  removeVirtualEventWaitListPersonsRequest: {},
}

const eventDetailsWaitListSlice = createSlice({
  name: 'virtualClassroomManagerEventDetailsWaitList',
  initialState,
  reducers: {
    listVirtualEventWaitListPersonsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListVirtualEventWaitListPersonsResponse>>
    ) {
      state.listVirtualEventWaitListPersons = action.payload
    },
    addVirtualEventWaitListPersonsRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.addVirtualEventWaitListPersonsRequest = action.payload
    },
    moveParticipantsToPreferredScheduleRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload>
    ) {
      state.moveParticipantsToPreferredScheduleRequest = action.payload
    },
    removeVirtualEventWaitListPersonsRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.removeVirtualEventWaitListPersonsRequest = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  listVirtualEventWaitListPersonsLoaded,
  addVirtualEventWaitListPersonsRequested,
  moveParticipantsToPreferredScheduleRequested,
  removeVirtualEventWaitListPersonsRequested,
} = eventDetailsWaitListSlice.actions
export default eventDetailsWaitListSlice.reducer

export const loadListVirtualEventWaitListPersons = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListVirtualEventWaitListPersons(eventId),
    },
    payloadAction: listVirtualEventWaitListPersonsLoaded,
  })
}

export const reloadListVirtualEventWaitListPersons = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListVirtualEventWaitListPersons(eventId),
    },
    payloadAction: listVirtualEventWaitListPersonsLoaded,
    getResultToKeep: ({ virtualClassroomManagerEventDetailsWaitList }) =>
      virtualClassroomManagerEventDetailsWaitList.listVirtualEventWaitListPersons.result,
    shouldShowGlobalLoading: true,
  })
}

const handleReloadListVirtualEventWaitListPersons =
  (response: ApiMiddlewarePayload, params: ReloadListVirtualEventWaitListPersonsParams): AppThunk =>
  async dispatch => {
    const { eventId, payloadAction } = params
    const { error } = response
    dispatch(payloadAction(response))
    if (error) return
    dispatch(reloadListVirtualEventWaitListPersons(eventId) as MiddlewareApiAction)
  }

export const addWaitListPersons = (params: AddVirtualEventWaitListPersonsModel): ApiAction => {
  const { eventId } = params
  return createApiAction({
    apiCalls: {
      request: () => addVirtualEventWaitListPersons(params),
    },
    payloadAction: addVirtualEventWaitListPersonsRequested,
    nextAction: {
      action: handleReloadListVirtualEventWaitListPersons,
      params: { eventId, payloadAction: addVirtualEventWaitListPersonsRequested },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const moveToPreferredSchedule = (
  params: MoveParticipantsToPreferredScheduleModel
): ApiAction => {
  const { eventId } = params
  return createApiAction({
    apiCalls: {
      request: () => moveParticipantsToPreferredSchedule(params),
    },
    payloadAction: moveParticipantsToPreferredScheduleRequested,
    nextAction: {
      action: handleReloadListVirtualEventWaitListPersons,
      params: { eventId, payloadAction: moveParticipantsToPreferredScheduleRequested },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const removeWaitListPersons = (
  params: RemoveVirtualEventWaitListPersonsModel
): ApiAction => {
  const { eventId } = params
  return createApiAction({
    apiCalls: {
      request: () => removeVirtualEventWaitListPersons(params),
    },
    payloadAction: removeVirtualEventWaitListPersonsRequested,
    nextAction: {
      action: handleReloadListVirtualEventWaitListPersons,
      params: { eventId, payloadAction: removeVirtualEventWaitListPersonsRequested },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}
